import React, { useState, useEffect, useRef } from 'react';

// Main Landing Component
const Home = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeAuthForm, setActiveAuthForm] = useState('signup'); // 'signup' or 'login'

    // Simple "database" for demonstration (in a real app, this would be a backend)
    const [registeredUsers, setRegisteredUsers] = useState({}); // Stores {email: {name, password}}

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const handleAuth = (event, type) => {
        event.preventDefault();

        let email, password, name;
        if (type === 'signup') {
            name = event.target.elements['signup-name'].value;
            email = event.target.elements['signup-email'].value;
            password = event.target.elements['signup-password'].value;
        } else { // login
            email = event.target.elements['login-email'].value;
            password = event.target.elements['login-password'].value;
        }

        const authMessageBox = document.getElementById('auth-message');

        // Basic validation
        if (!email || !password || (type === 'signup' && !name)) {
            displayAuthMessage(authMessageBox, 'Please fill in all required fields.', 'red');
            return;
        }

        if (!validateEmail(email)) {
            displayAuthMessage(authMessageBox, 'Please enter a valid email address.', 'red');
            return;
        }

        if (type === 'signup') {
            if (password.length < 6) {
                displayAuthMessage(authMessageBox, 'Password must be at least 6 characters long.', 'red');
                return;
            }
            if (registeredUsers[email]) {
                displayAuthMessage(authMessageBox, 'This email is already registered. Please log in.', 'red');
            } else {
                setRegisteredUsers(prevUsers => ({
                    ...prevUsers,
                    [email]: { name: name, password: password }
                }));
                displayAuthMessage(authMessageBox, 'Sign up successful! You can now log in.', 'green');
                event.target.reset(); // Clear form
                setActiveAuthForm('login'); // Switch to login form
            }
        } else { // login
            if (registeredUsers[email] && registeredUsers[email].password === password) {
                displayAuthMessage(authMessageBox, `Login successful! Welcome, ${registeredUsers[email].name}!`, 'green');
                event.target.reset(); // Clear form
                // In a real app, you'd redirect to a dashboard or set user context
            } else {
                displayAuthMessage(authMessageBox, 'Invalid email or password.', 'red');
            }
        }
    };

    const displayAuthMessage = (element, message, type) => {
        element.textContent = message;
        element.classList.remove('hidden', 'bg-green-100', 'text-green-800', 'bg-red-100', 'text-red-800');
        if (type === 'green') {
            element.classList.add('bg-green-100', 'text-green-800');
        } else {
            element.classList.add('bg-red-100', 'text-red-800');
        }
        setTimeout(() => {
            element.classList.add('hidden');
        }, 5000); // Hide after 5 seconds
    };

    const validateEmail = (email) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    return (
        <div className="text-gray-800">
            {/* The custom scrollbar styles are here. Tailwind CSS is loaded via CDN in public/index.html */}
            <style>
                {`
                body {
                    font-family: 'Inter', sans-serif;
                    background-color: #f8fafc; /* Light blue-gray */
                }
                /* Custom scrollbar for better aesthetics */
                ::-webkit-scrollbar {
                    width: 8px;
                }
                ::-webkit-scrollbar-track {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                ::-webkit-scrollbar-thumb {
                    background: #60a5fa; /* Blue-500 */
                    border-radius: 10px;
                }
                ::-webkit-scrollbar-thumb:hover {
                    background: #3b82f6; /* Blue-600 */
                }

                /* Animation Keyframes for general fade-in-up */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Animation Keyframes for slide-in-from-right */
                @keyframes slideInFromRight {
                    from {
                        opacity: 0;
                        transform: translateX(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                .animate-fade-in-up {
                    animation: fadeInUp 0.6s ease-out forwards; /* forwards keeps the final state */
                    opacity: 0; /* Initial hidden state before animation */
                }

                .animate-slide-in-from-right {
                    animation: slideInFromRight 0.7s ease-out forwards;
                    opacity: 0; /* Initial hidden state */
                    transform: translateX(50px); /* Initial position off-screen right */
                }

                /* New animations for circular layout */
                @keyframes fadeInAndScale {
                    from {
                        opacity: 0;
                        transform: scale(0.8);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .animate-fade-in-scale {
                    animation: fadeInAndScale 0.7s ease-out forwards;
                    opacity: 0;
                    transform: scale(0.8);
                }

                @keyframes drawLine {
                    from {
                        stroke-dashoffset: 1000;
                    }
                    to {
                        stroke-dashoffset: 0;
                    }
                }

                .animate-draw-line {
                    stroke-dasharray: 1000;
                    stroke-dashoffset: 1000;
                    animation: drawLine 1s ease-out forwards;
                }
                `}
            </style>

            {/* Header */}
            <header className="bg-white shadow-sm py-4 px-6 md:px-10 sticky top-0 z-50 rounded-b-lg">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <a href="#" className="text-2xl font-bold text-blue-600 rounded-md p-2 hover:text-blue-700 transition-colors">
                        NERUS
                    </a>
                    <nav className="hidden md:flex space-x-6">
                        <a href="#mutual-funds" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors">Mutual Funds</a>
                        <a href="#sip" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors">SIP</a>
                        <a href="#swp" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors">SWP</a>
                        <a href="#insurance" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors">Insurance</a>
                        <a href="#auth-section" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors">Login / Sign Up</a>
                    </nav>
                    {/* Mobile Menu Button */}
                    <button id="mobile-menu-button" className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" onClick={toggleMobileMenu}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                    </button>
                </div>
                {/* Mobile Menu Overlay */}
                <div id="mobile-menu" className={`${isMobileMenuOpen ? '' : 'hidden'} md:hidden fixed inset-0 bg-black bg-opacity-50 z-40`}>
                    <div className="bg-white w-64 h-full p-6 shadow-lg rounded-r-lg">
                        <button id="close-mobile-menu" className="absolute top-4 right-4 p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" onClick={closeMobileMenu}>
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                        <nav className="flex flex-col space-y-4 mt-10">
                            <a href="#mutual-funds" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors" onClick={closeMobileMenu}>Mutual Funds</a>
                            <a href="#sip" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors" onClick={closeMobileMenu}>SIP</a>
                            <a href="#swp" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors" onClick={closeMobileMenu}>SWP</a>
                            <a href="#insurance" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors" onClick={closeMobileMenu}>Insurance</a>
                            <a href="#auth-section" className="text-gray-700 hover:text-blue-600 font-medium rounded-md px-3 py-2 transition-colors" onClick={closeMobileMenu}>Login / Sign Up</a>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 md:py-24 text-center rounded-b-lg shadow-lg">
                <div className="max-w-4xl mx-auto px-6">
                    <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 animate-fade-in-up">
                        Secure Your Financial Future with NERUS
                    </h1>
                    <p className="text-lg md:text-xl mb-8 opacity-90 animate-fade-in-up delay-200">
                        Explore our tailored solutions for Mutual Funds, SIP, SWP, and Insurance to achieve your financial goals.
                    </p>
                    <a href="#auth-section" className="inline-block bg-white text-blue-700 hover:bg-blue-100 font-bold py-3 px-8 rounded-full shadow-lg transform transition-all duration-300 hover:scale-105 animate-fade-in-up delay-400">
                        Get Started Today
                    </a>
                </div>
            </section>

            {/* Features/Offerings Section */}
            <section className="py-16 md:py-20 bg-white rounded-lg shadow-md mx-4 md:mx-auto max-w-7xl mt-8">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">Our Comprehensive Solutions</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {/* Mutual Funds Card */}
                        <SolutionCard
                            id="mutual-funds"
                            icon={
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                                    <path d="M2 17l10 5 10-5"></path>
                                    <path d="M2 12l10 5 10-5"></path>
                                </svg>
                            }
                            iconColor="text-blue-500"
                            title="Mutual Funds"
                            description="Diversify your investments and achieve long-term growth with our expertly curated mutual fund portfolios."
                            buttonText="Explore Funds"
                            buttonColor="bg-blue-600 hover:bg-blue-700"
                        />

                        {/* SIP Card */}
                        <SolutionCard
                            id="sip"
                            icon={
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                    <path d="M12 16L12 8"></path>
                                    <path d="M8 12L12 8 16 12"></path>
                                </svg>
                            }
                            iconColor="text-green-500"
                            title="Systematic Investment Plan (SIP)"
                            description="Start investing small, regular amounts and benefit from rupee cost averaging and compounding."
                            buttonText="Start Your SIP"
                            buttonColor="bg-green-600 hover:bg-green-700"
                        />

                        {/* SWP Card - NEW */}
                        <SolutionCard
                            id="swp"
                            icon={
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 9v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9"></path>
                                    <path d="M10 12l2 2 2-2"></path>
                                    <path d="M12 14V4"></path>
                                    <path d="M15 2H9"></path>
                                </svg>
                            }
                            iconColor="text-yellow-500"
                            title="Systematic Withdrawal Plan (SWP)"
                            description="Receive regular income from your investments while maintaining growth potential."
                            buttonText="Plan Your SWP"
                            buttonColor="bg-yellow-600 hover:bg-yellow-700"
                        />

                        {/* Insurance Card */}
                        <SolutionCard
                            id="insurance"
                            icon={
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                                    <path d="M12 12V6"></path>
                                    <path d="M12 18V12"></path>
                                </svg>
                            }
                            iconColor="text-purple-500"
                            title="Insurance Solutions"
                            description="Protect yourself and your loved ones with comprehensive life, health, and general insurance plans."
                            buttonText="Get Insured"
                            buttonColor="bg-purple-600 hover:bg-purple-700"
                        />
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section - NEW COMPONENT */}
            <WhyNerusSection />

            {/* Login/Sign Up Section */}
            <section id="auth-section" className="py-16 md:py-20 bg-gradient-to-br from-blue-700 to-blue-900 text-white rounded-lg shadow-md mx-4 md:mx-auto max-w-7xl mt-8 mb-8">
                <div className="max-w-md mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6" id="auth-title">
                        {activeAuthForm === 'signup' ? 'Sign Up for NERUS' : 'Login to NERUS'}
                    </h2>
                    <p className="text-lg opacity-90 mb-10" id="auth-subtitle">
                        {activeAuthForm === 'signup' ? 'Join us to start securing your financial future.' : 'Welcome back! Please log in to your account.'}
                    </p>

                    <div className="flex justify-center mb-8">
                        <button
                            id="show-signup"
                            className={`px-6 py-3 rounded-l-full font-semibold text-lg transition-colors duration-300 border-2 border-r-0 border-blue-400 ${activeAuthForm === 'signup' ? 'bg-blue-600 text-white' : 'bg-transparent text-blue-100 hover:bg-blue-600'}`}
                            onClick={() => setActiveAuthForm('signup')}
                        >
                            Sign Up
                        </button>
                        <button
                            id="show-login"
                            className={`px-6 py-3 rounded-r-full font-semibold text-lg transition-colors duration-300 border-2 border-l-0 border-blue-400 ${activeAuthForm === 'login' ? 'bg-blue-600 text-white' : 'bg-transparent text-blue-100 hover:bg-blue-600'}`}
                            onClick={() => setActiveAuthForm('login')}
                        >
                            Login
                        </button>
                    </div>

                    {/* Sign Up Form */}
                    {activeAuthForm === 'signup' && (
                        <form id="signup-form" className="space-y-6 bg-white p-8 rounded-xl shadow-lg text-gray-800" onSubmit={(e) => handleAuth(e, 'signup')}>
                            <div>
                                <label htmlFor="signup-name" className="block text-left text-sm font-medium text-gray-700 mb-2">Name</label>
                                <input type="text" id="signup-name" name="signup-name" required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Your Name" />
                            </div>
                            <div>
                                <label htmlFor="signup-email" className="block text-left text-sm font-medium text-gray-700 mb-2">Email</label>
                                <input type="email" id="signup-email" name="signup-email" required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="your.email@example.com" />
                            </div>
                            <div>
                                <label htmlFor="signup-password" className="block text-left text-sm font-medium text-gray-700 mb-2">Password</label>
                                <input type="password" id="signup-password" name="signup-password" required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Min 6 characters" />
                            </div>
                            <button type="submit"
                                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-md hover:bg-blue-700 transition-colors shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                                Sign Up
                            </button>
                        </form>
                    )}

                    {/* Login Form */}
                    {activeAuthForm === 'login' && (
                        <form id="login-form" className="space-y-6 bg-white p-8 rounded-xl shadow-lg text-gray-800" onSubmit={(e) => handleAuth(e, 'login')}>
                            <div>
                                <label htmlFor="login-email" className="block text-left text-sm font-medium text-gray-700 mb-2">Email</label>
                                <input type="email" id="login-email" name="login-email" required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="your.email@example.com" />
                            </div>
                            <div>
                                <label htmlFor="login-password" className="block text-left text-sm font-medium text-gray-700 mb-2">Password</label>
                                <input type="password" id="login-password" name="login-password" required
                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Your password" />
                            </div>
                            <button type="submit"
                                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-md hover:bg-blue-700 transition-colors shadow-lg transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                                Login
                            </button>
                        </form>
                    )}

                    {/* Message Box for Auth */}
                    <div id="auth-message" className="mt-6 p-4 bg-green-100 text-green-800 rounded-md hidden">
                        {/* Messages will appear here */}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-800 text-gray-300 py-8 px-6 md:px-10 rounded-t-lg">
                <div className="max-w-7xl mx-auto text-center md:flex md:justify-between md:items-center">
                    <p className="mb-4 md:mb-0">&copy; 2025 NERUS. All rights reserved.</p>
                    <div className="flex justify-center space-x-6">
                        <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
                        <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
                        <a href="#" className="hover:text-white transition-colors">Disclaimer</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

// SolutionCard Component (unchanged)
const SolutionCard = ({ id, icon, iconColor, title, description, buttonText, buttonColor }) => (
    <div id={id} className="bg-gray-50 p-8 rounded-xl shadow-md border border-gray-200 hover:shadow-lg transform transition-transform duration-300 hover:-translate-y-2">
        <div className={`${iconColor} mb-4`}>
            {icon}
        </div>
        <h3 className="text-2xl font-semibold text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-600 leading-relaxed mb-6">
            {description}
        </p>
        <a href="#auth-section" className={`inline-block ${buttonColor} text-white font-medium py-2 px-6 rounded-full transition-colors shadow-md`}>
            {buttonText}
        </a>
    </div>
);


// WhyNerusSection Component - NEW
const WhyNerusSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Re-trigger animation every time it enters the viewport
                if (entry.isIntersecting) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false); // Reset state when leaving viewport
                }
            },
            {
                threshold: 0.2 // Trigger when 20% of the section is visible
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    const cardsData = [
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <path d="M22 4L12 14.01l-3-3"></path>
                </svg>
            ),
            title: "Expert Guidance",
            description: "Personalized advice from certified financial planners."
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                </svg>
            ),
            title: "Long-Term Growth",
            description: "Strategies designed for sustainable wealth creation."
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                    <polyline points="13 2 13 9 20 9"></polyline>
                </svg>
            ),
            title: "Transparent Process",
            description: "Clear, concise, and easy-to-understand investment options."
        },
        {
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
                    <line x1="4" y1="22" x2="4" y2="15"></line>
                </svg>
            ),
            title: "Robust Security",
            description: "Your data and investments are always protected."
        }
    ];

    return (
        <section ref={sectionRef} className="bg-blue-50 py-16 md:py-20 rounded-lg shadow-md mx-4 md:mx-auto max-w-7xl mt-8">
            <div className="relative w-full max-w-4xl mx-auto px-6 h-[500px] md:h-[600px] flex items-center justify-center">
                {/* Central "Why NERUS?" element */}
                <div className={`absolute z-10 p-8 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center text-center
                                w-48 h-48 md:w-64 md:h-64 flex-col
                                ${isVisible ? 'animate-fade-in-scale' : 'opacity-0 scale-80'}`}
                    style={{ animationDelay: '0s' }} // Title appears first
                >
                    <h2 className="text-2xl md:text-3xl font-bold leading-tight">Why Choose NERUS?</h2>
                </div>

                {/* SVG for connecting lines */}
                <svg className="absolute inset-0 w-full h-full z-0" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid meet">
                    {/* Define center point for lines (relative to SVG viewBox) */}
                    {/* The center of the SVG is 500, 500 */}
                    {cardsData.map((_, i) => {
                        // Calculate card positions relative to SVG viewBox (adjust as needed)
                        // These are approximate coordinates to connect to the center of the cards
                        // and are based on a rough visual alignment for a circular layout.
                        // For precise dynamic positioning, you'd need to measure card positions.
                        const cardPositions = [
                            { x: 750, y: 250 }, // Top-Right
                            { x: 750, y: 750 }, // Bottom-Right
                            { x: 250, y: 750 }, // Bottom-Left
                            { x: 250, y: 250 }  // Top-Left
                        ];
                        const startX = 500; // Center of SVG
                        const startY = 500; // Center of SVG
                        const endX = cardPositions[i].x;
                        const endY = cardPositions[i].y;

                        return (
                            <line
                                key={`line-${i}`}
                                x1={startX} y1={startY}
                                x2={endX} y2={endY}
                                stroke="#60a5fa" // Tailwind blue-400
                                strokeWidth="3"
                                className={isVisible ? 'animate-draw-line' : ''}
                                style={{ animationDelay: `${0.5 + i * 0.2}s` }} // Lines draw after title, staggered
                            />
                        );
                    })}
                </svg>


                {/* Individual Cards positioned absolutely around the center */}
                {cardsData.map((card, index) => (
                    <div
                        key={index}
                        // Positioning classes for a rough circular/diamond layout
                        className={`absolute w-48 h-48 md:w-56 md:h-56 p-6 bg-white rounded-xl shadow-md border border-blue-100 flex flex-col items-center justify-center text-center
                                    ${isVisible ? 'animate-fade-in-scale' : 'opacity-0 scale-80'}`}
                        style={{
                            // Calculate positions for a circular layout
                            // These values are fine-tuned for a visual balance.
                            // Adjust 'top', 'left', 'transform' for different layouts.
                            top: index === 0 ? '0%' : index === 1 ? 'auto' : index === 2 ? 'auto' : '0%',
                            right: index === 0 ? '0%' : index === 1 ? '0%' : 'auto',
                            bottom: index === 0 ? 'auto' : index === 1 ? '0%' : index === 2 ? '0%' : 'auto',
                            left: index === 0 ? 'auto' : index === 1 ? 'auto' : index === 2 ? '0%' : '0%',
                            transform: index === 0 ? 'translate(25%, 25%)' : // Top-right
                                       index === 1 ? 'translate(25%, -25%)' : // Bottom-right
                                       index === 2 ? 'translate(-25%, -25%)' : // Bottom-left
                                       'translate(-25%, 25%)', // Top-left
                            animationDelay: `${0.2 + index * 0.2}s`, // Staggered animation for cards
                        }}
                    >
                        <div className="text-blue-500 mb-2">
                            {card.icon}
                        </div>
                        <h3 className="text-xl font-semibold mb-1">{card.title}</h3>
                        <p className="text-gray-600 text-sm">{card.description}</p>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default Home;
